import { useState, useId, useEffect, useMemo } from 'react';
import { ArrowRightLeft, CheckCircle2 } from 'lucide-react';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { useMoveBookingRoom } from '@/hooks/api/useBookings';
import { useUnits } from '@/hooks/api/useProperties';
import type { BookingEntry } from '@/services/api/frontdesk.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const MOVE_REASONS = [
  { value: '', label: 'Select a reason (optional)' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'complaint', label: 'Guest Complaint' },
  { value: 'maintenance', label: 'Maintenance Required' },
  { value: 'request', label: 'Guest Request' },
  { value: 'other', label: 'Other' },
];

interface UnitOption {
  id: string;
  name: string;
  type: string;
  capacity: number;
  housekeepingStatus: string;
  isActive: boolean;
}

interface RoomMoveSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingEntry;
}

export function RoomMoveSlideOver({ isOpen, onClose, booking }: RoomMoveSlideOverProps) {
  const [selectedUnit, setSelectedUnit] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const unitSelectId = useId();
  const reasonSelectId = useId();
  const customReasonId = useId();

  const propertyId = booking.unit?.propertyId;
  const unitsQuery = useUnits(propertyId);
  const moveMutation = useMoveBookingRoom();

  // Available rooms: active, clean/inspected, not the current unit
  const availableUnits: UnitOption[] = useMemo(() => {
    const all: UnitOption[] = unitsQuery.data ?? [];
    return all.filter(
      (u) =>
        u.isActive &&
        u.id !== booking.unitId &&
        (u.housekeepingStatus === 'clean' || u.housekeepingStatus === 'inspected'),
    );
  }, [unitsQuery.data, booking.unitId]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSelectedUnit('');
      setReason('');
      setCustomReason('');
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedUnit) {
      setError('Please select a new room.');
      return;
    }
    setError('');

    const reasonText = reason === 'other' ? customReason.trim() : reason;

    try {
      const movePayload: { newUnitId: string; reason?: string } = {
        newUnitId: selectedUnit,
      };
      if (reasonText) movePayload.reason = reasonText;
      await moveMutation.mutateAsync({
        id: booking.id,
        payload: movePayload,
      });
      setSuccessMessage('Room move completed successfully.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Room move failed. Please try again.');
    }
  };

  const currentUnit = booking.unit;

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title="Move Room">
      <div className="space-y-5">
        {/* Messages */}
        {successMessage && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-center gap-2" role="status" aria-live="polite">
            <CheckCircle2 size={16} className="text-green-600 shrink-0" aria-hidden="true" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3" role="alert">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Guest info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Guest</p>
          <p className="text-sm font-medium text-gray-900">
            {booking.guest?.fullName ?? 'Unknown Guest'}
          </p>
        </div>

        {/* Current room */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Room</p>
          <p className="text-sm font-medium text-gray-900">
            {currentUnit?.name ?? booking.unitId.slice(0, 8)}
          </p>
          {currentUnit?.type && (
            <p className="text-xs text-gray-500">{currentUnit.type}</p>
          )}
        </div>

        {/* New room selector */}
        <div>
          <label htmlFor={unitSelectId} className="block text-sm font-medium text-gray-700 mb-1">
            New Room <span className="text-gray-400 font-normal">(required)</span>
          </label>
          <select
            id={unitSelectId}
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a room</option>
            {availableUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} -- {u.type} (max {u.capacity}) -- {u.housekeepingStatus}
              </option>
            ))}
          </select>
          {unitsQuery.isLoading && (
            <p className="text-xs text-gray-400 mt-1">Loading rooms...</p>
          )}
          {!unitsQuery.isLoading && availableUnits.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">No clean/inspected rooms available in this property.</p>
          )}
        </div>

        {/* Reason */}
        <div>
          <label htmlFor={reasonSelectId} className="block text-sm font-medium text-gray-700 mb-1">
            Reason
          </label>
          <select
            id={reasonSelectId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {MOVE_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {reason === 'other' && (
          <div>
            <label htmlFor={customReasonId} className="block text-sm font-medium text-gray-700 mb-1">
              Specify Reason
            </label>
            <input
              id={customReasonId}
              type="text"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter reason for room move..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}

        {/* Visual: Current -> New */}
        {selectedUnit && (
          <div className="flex items-center justify-center gap-4 py-3 bg-primary-50 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-gray-500">Current</p>
              <p className="text-sm font-semibold text-gray-900">
                {currentUnit?.name ?? booking.unitId.slice(0, 8)}
              </p>
            </div>
            <ArrowRightLeft size={20} className="text-primary-600" aria-hidden="true" />
            <div className="text-center">
              <p className="text-xs text-gray-500">New</p>
              <p className="text-sm font-semibold text-primary-700">
                {availableUnits.find((u) => u.id === selectedUnit)?.name ?? selectedUnit.slice(0, 8)}
              </p>
            </div>
          </div>
        )}

        <div className="pt-2">
          <Button
            type="button"
            fullWidth
            onClick={handleSubmit}
            isLoading={moveMutation.isPending}
            disabled={!selectedUnit || moveMutation.isPending}
          >
            <ArrowRightLeft size={16} aria-hidden="true" />
            Confirm Move
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}
